import { alpha } from '@mui/material/styles'
import type { DeviceStatus } from '../data/devices.ts'
// biome-ignore lint/correctness/noPrivateImports: This is meant to be imported here
import { xwiPalette } from './xwiPalette.ts'

declare module '@mui/material/styles' {
	interface PaletteOptions {
		brand: { main: string; contrastText: string }
		mapMarker: { bg: string; fg: string }
		ownLocation: {
			fill: string
			outline: string
			accuracyFill: string
			accuracyOutline: string
		}
		lightningReport: {
			reportGeometry: string
			customPoint: string
			errorEllipse: string
			fadedErrorEllipse: string
			highlightedErrorEllipse: string
		}
		meteogram: {
			precip: (percent: number) => string
			night: string
		}
		meteogramOld: {
			storm: string
			brisk: string
			calm: string
			still: string
			temperature: string
			precip: string
			probability: {
				high: { main: string; contrastText: string }
				low: { main: string; contrastText: string }
				veryLow: { main: string; contrastText: string }
			}
		}
		devices: { badStatus: string } & Record<DeviceStatus['status'], { main: string; icon: string; contrastText: string; divider: string }>
		alerts: {
			alarm: string
			warning: string
			info: string
			clear: string
			hail: string
			guidelines: string
			alertStack: {
				active: {
					alarm: {
						filled: { icon: string; font: string; bg: string }
						outlined: { icon: string; font: string; bg: string }
						measurement: { icon: string; font: string; bg: string }
					}
					warning: {
						filled: { icon: string; font: string; bg: string }
						outlined: { icon: string; font: string; bg: string }
						measurement: { icon: string; font: string; bg: string }
					}
					info: {
						filled: { icon: string; font: string; bg: string }
						outlined: { icon: string; font: string; bg: string }
						measurement: { icon: string; font: string; bg: string }
					}
				}
				inactive: {
					filled: { icon: string; font: string; bg: string }
					outlined: { icon: string; font: string; bg: string }
					measurement: { icon: string; font: string; bg: string }
				}
			}
		}
		/** To see and edit graph token usages, see the graphGroups file.
		 * @see {@link file://./../features/measurements/graph/graphGroups.ts} */
		graph: {
			bluePrimary: string
			greenDark: string
			greenLight: string
			greenPrimary: string
			greyDark: string
			greyLight: string
			greyMedium: string
			grid: string
			stroke: string
			alert: string
			orangeLight: string
			orangePrimary: string
			redDark: string
			redPrimary: string
			tealDark: string
			tealDarker: string
			tealPrimary: string
			transparent: string
			violetPrimary: string
			yellowPrimary: string
			referenceLine: string
		}
		avatar: {
			text: string
			bg: string
			hoverBg: string
		}
		impersonation: {
			main: string
			contrastText: string
		}
		subscriptionExpiration: {
			warning60Days: string
			warning30Days: string
		}
		subscriptionStatus: {
			active: string
			trial: string
			canceled: string
			created: string
			suspended: string
			expired: string
			pendingCancelation: string
		}
		recommendedActions: {
			highlight: string
		}
		iconStack: {
			filled: {
				bgColor: string
				iconColor: string
				fontColor: string
			}
			outlined: {
				bgColor: string
				iconColor: string
				fontColor: string
			}
		}
		timeline: { greyzone: string }
		animatableLayer: string
		locationQualityWarning: string
		mapStyleSelected: string
	}
}

export const paletteExtrasLight = {
	brand: { main: xwiPalette.thunder[900], contrastText: xwiPalette.common.white },
	mapMarker: { bg: xwiPalette.grey[900], fg: xwiPalette.grey[100] },
	ownLocation: {
		fill: xwiPalette.blue[600],
		outline: xwiPalette.common.white,
		accuracyFill: alpha(xwiPalette.blue[600], 0.1),
		accuracyOutline: xwiPalette.blue[600],
	},
	lightningReport: {
		reportGeometry: xwiPalette.blue[500],
		customPoint: xwiPalette.blue[600],
		errorEllipse: alpha(xwiPalette.orange[600], 0.25),
		fadedErrorEllipse: alpha(xwiPalette.orange[600], 0.08),
		highlightedErrorEllipse: alpha(xwiPalette.orange[500], 0.5),
	},
	meteogram: {
		precip: (percent: number) => alpha(xwiPalette.teal[500], Math.min(1, 0.25 * (percent / 100))),
		night: xwiPalette.blue[50],
	},
	meteogramOld: {
		storm: xwiPalette.red[500],
		brisk: xwiPalette.yellow[600],
		calm: xwiPalette.green[500],
		still: xwiPalette.grey[300],
		temperature: xwiPalette.grey[600],
		precip: xwiPalette.blue[300],
		probability: {
			high: { main: xwiPalette.blue[600], contrastText: xwiPalette.common.white },
			low: { main: xwiPalette.blue[100], contrastText: xwiPalette.blue[600] },
			veryLow: { main: xwiPalette.common.white, contrastText: xwiPalette.blue[600] },
		},
	},
	devices: {
		badStatus: xwiPalette.grey[500],
		OK: {
			main: xwiPalette.common.white,
			icon: xwiPalette.grey[700],
			contrastText: xwiPalette.grey[800],
			divider: xwiPalette.grey[200],
		},
		DEGRADED: {
			main: xwiPalette.orange[600],
			icon: xwiPalette.orange[600],
			contrastText: xwiPalette.common.white,
			divider: xwiPalette.common.white,
		},
		ERROR: {
			main: xwiPalette.red[600],
			icon: xwiPalette.red[600],
			contrastText: xwiPalette.common.white,
			divider: xwiPalette.common.white,
		},
		OFFLINE: {
			main: xwiPalette.grey[400],
			icon: xwiPalette.grey[400],
			contrastText: xwiPalette.common.white,
			divider: xwiPalette.common.white,
		},
		UNKNOWN: {
			main: xwiPalette.grey[400],
			icon: xwiPalette.grey[400],
			contrastText: xwiPalette.common.white,
			divider: xwiPalette.common.white,
		},
	},
	alerts: {
		alarm: xwiPalette.red[600],
		warning: xwiPalette.orange[500],
		info: xwiPalette.yellow[600],
		clear: xwiPalette.grey[700],
		hail: xwiPalette.violet[500],
		guidelines: xwiPalette.yellow[50],
		alertStack: {
			active: {
				alarm: {
					filled: { icon: 'background.paper', font: 'background.paper', bg: xwiPalette.red[600] },
					outlined: { icon: xwiPalette.red[600], font: xwiPalette.red[600], bg: 'background.paper' },
					measurement: { icon: 'background.paper', font: 'background.paper', bg: xwiPalette.red[600] },
				},
				warning: {
					filled: { icon: xwiPalette.orange[600], font: xwiPalette.orange[600], bg: xwiPalette.orange[100] },
					outlined: { icon: xwiPalette.orange[500], font: xwiPalette.orange[600], bg: 'background.paper' },
					measurement: { icon: xwiPalette.orange[600], font: xwiPalette.orange[600], bg: xwiPalette.orange[100] },
				},
				info: {
					filled: { icon: xwiPalette.yellow[600], font: xwiPalette.yellow[700], bg: xwiPalette.yellow[200] },
					outlined: { icon: xwiPalette.yellow[600], font: xwiPalette.yellow[600], bg: 'background.paper' },
					measurement: { icon: xwiPalette.orange[600], font: xwiPalette.orange[600], bg: xwiPalette.orange[100] },
				},
			},
			inactive: {
				filled: { icon: xwiPalette.grey[400], font: 'background.paper', bg: 'background.paper' },
				outlined: { icon: 'background.paper', font: xwiPalette.grey[400], bg: xwiPalette.grey[400] },
				measurement: { icon: 'background.paper', font: 'background.paper', bg: xwiPalette.grey[400] },
			},
		},
	},
	graph: {
		bluePrimary: xwiPalette.blue[800],
		greenDark: xwiPalette.green[800],
		greenLight: xwiPalette.green[400],
		greenPrimary: xwiPalette.green[600],
		greyDark: xwiPalette.grey[900],
		greyLight: xwiPalette.grey[600],
		greyMedium: xwiPalette.grey[800],
		grid: xwiPalette.grey[200],
		stroke: xwiPalette.grey[500],
		alert: xwiPalette.red[600],
		orangeLight: xwiPalette.orange[200],
		orangePrimary: xwiPalette.orange[600],
		redDark: xwiPalette.red[800],
		redPrimary: xwiPalette.red[600],
		tealDark: xwiPalette.teal[600],
		tealDarker: xwiPalette.teal[800],
		tealPrimary: xwiPalette.teal[500],
		transparent: 'transparent',
		violetPrimary: xwiPalette.violet[600],
		yellowPrimary: xwiPalette.yellow[600],
		referenceLine: xwiPalette.grey[800],
	},
	avatar: {
		text: xwiPalette.thunder[900],
		bg: xwiPalette.yellow[500],
		hoverBg: xwiPalette.yellow[700],
	},
	impersonation: {
		main: xwiPalette.yellow[500],
		contrastText: xwiPalette.common.black,
	},
	subscriptionExpiration: {
		warning60Days: xwiPalette.orange[200],
		warning30Days: xwiPalette.yellow[200],
	},
	subscriptionStatus: {
		active: xwiPalette.green[500],
		trial: xwiPalette.violet[600],
		canceled: xwiPalette.red[600],
		created: xwiPalette.yellow[600],
		suspended: xwiPalette.grey[500],
		expired: xwiPalette.grey[500],
		pendingCancelation: xwiPalette.grey[500],
	},
	recommendedActions: {
		highlight: xwiPalette.yellow[800],
	},
	iconStack: {
		filled: {
			bgColor: xwiPalette.grey[400],
			iconColor: 'background.paper',
			fontColor: 'background.paper',
		},
		outlined: {
			bgColor: 'background.paper',
			iconColor: xwiPalette.grey[400],
			fontColor: 'background.paper',
		},
	},
	timeline: { greyzone: xwiPalette.grey[300] },
	animatableLayer: xwiPalette.green[400],
	locationQualityWarning: xwiPalette.grey[300],
	mapStyleSelected: xwiPalette.blue[600],
}

export const paletteExtrasDark = {
	brand: { main: xwiPalette.thunder[900], contrastText: xwiPalette.common.white },
	mapMarker: { bg: xwiPalette.grey[200], fg: xwiPalette.grey[900] },
	ownLocation: {
		fill: xwiPalette.blue[600],
		outline: xwiPalette.common.white,
		accuracyFill: alpha(xwiPalette.blue[600], 0.1),
		accuracyOutline: xwiPalette.blue[600],
	},
	lightningReport: {
		reportGeometry: xwiPalette.blue[500],
		customPoint: xwiPalette.blue[600],
		errorEllipse: alpha(xwiPalette.orange[600], 0.25),
		fadedErrorEllipse: alpha(xwiPalette.orange[600], 0.08),
		highlightedErrorEllipse: alpha(xwiPalette.orange[500], 0.5),
	},
	meteogram: {
		precip: (percent: number) => alpha(xwiPalette.teal[500], Math.min(1, 0.25 * (percent / 100))),
		night: xwiPalette.greyDark[50],
	},
	meteogramOld: {
		storm: xwiPalette.red[500],
		brisk: xwiPalette.yellow[600],
		calm: xwiPalette.green[600],
		still: xwiPalette.greyDark[300],
		temperature: xwiPalette.greyDark[600],
		precip: xwiPalette.blue[300],
		probability: {
			high: { main: xwiPalette.blue[600], contrastText: xwiPalette.common.white },
			low: { main: xwiPalette.blue[100], contrastText: xwiPalette.blue[600] },
			veryLow: { main: xwiPalette.greyDark[50], contrastText: xwiPalette.blue[600] },
		},
	},
	devices: {
		badStatus: xwiPalette.grey[300],
		OK: {
			main: xwiPalette.greyDark[75] ?? '',
			icon: xwiPalette.green[600],
			contrastText: xwiPalette.greyDark[500],
			divider: xwiPalette.greyDark[200],
		},
		DEGRADED: {
			main: xwiPalette.orange[600],
			icon: xwiPalette.orange[600],
			contrastText: xwiPalette.common.white,
			divider: xwiPalette.common.white,
		},
		ERROR: {
			main: xwiPalette.red[600],
			icon: xwiPalette.red[600],
			contrastText: xwiPalette.common.white,
			divider: xwiPalette.common.white,
		},
		OFFLINE: {
			main: xwiPalette.greyDark[200],
			icon: xwiPalette.grey[400],
			contrastText: xwiPalette.common.white,
			divider: xwiPalette.common.white,
		},
		UNKNOWN: {
			main: xwiPalette.greyDark[200],
			icon: xwiPalette.grey[400],
			contrastText: xwiPalette.common.white,
			divider: xwiPalette.common.white,
		},
	},
	alerts: {
		alarm: xwiPalette.red[600],
		warning: xwiPalette.orange[500],
		info: xwiPalette.yellow[600],
		clear: xwiPalette.grey.A100,
		hail: xwiPalette.violet[500],
		guidelines: xwiPalette.yellow[900],
		alertStack: {
			active: {
				alarm: {
					filled: { icon: 'background.paper', font: 'background.paper', bg: xwiPalette.red[600] },
					outlined: { icon: xwiPalette.red[600], font: xwiPalette.red[600], bg: 'background.paper' },
					measurement: { icon: 'background.paper', font: 'background.paper', bg: xwiPalette.red[600] },
				},
				warning: {
					filled: { icon: xwiPalette.orange[600], font: xwiPalette.orange[600], bg: xwiPalette.orange[100] },
					outlined: { icon: xwiPalette.orange[500], font: xwiPalette.orange[600], bg: 'background.paper' },
					measurement: { icon: xwiPalette.orange[600], font: xwiPalette.orange[600], bg: xwiPalette.orange[100] },
				},
				info: {
					filled: { icon: xwiPalette.yellow[600], font: xwiPalette.yellow[700], bg: xwiPalette.yellow[200] },
					outlined: { icon: xwiPalette.yellow[600], font: xwiPalette.yellow[600], bg: 'background.paper' },
					measurement: { icon: xwiPalette.orange[600], font: xwiPalette.orange[600], bg: xwiPalette.orange[100] },
				},
			},
			inactive: {
				filled: { icon: xwiPalette.grey[400], font: 'background.paper', bg: 'background.paper' },
				outlined: { icon: 'background.paper', font: xwiPalette.grey[400], bg: xwiPalette.grey[400] },
				measurement: { icon: 'background.paper', font: 'background.paper', bg: xwiPalette.grey[400] },
			},
		},
	},
	graph: {
		bluePrimary: xwiPalette.blue[500],
		greenDark: xwiPalette.green[300],
		greenLight: xwiPalette.green[700],
		greenPrimary: xwiPalette.green[500],
		greyDark: xwiPalette.grey[200],
		greyLight: xwiPalette.grey[600],
		greyMedium: xwiPalette.grey[300],
		grid: xwiPalette.grey[800],
		stroke: xwiPalette.grey[500],
		alert: xwiPalette.red[600],
		orangeLight: xwiPalette.orange[300],
		orangePrimary: xwiPalette.orange[500],
		redDark: xwiPalette.red[400],
		redPrimary: xwiPalette.red[600],
		tealDark: xwiPalette.teal[600],
		tealDarker: xwiPalette.teal[300],
		tealPrimary: xwiPalette.teal[800],
		transparent: 'transparent',
		violetPrimary: xwiPalette.violet[500],
		yellowPrimary: xwiPalette.yellow[600],
		referenceLine: xwiPalette.grey[800],
	},
	avatar: {
		text: xwiPalette.thunder[900],
		bg: xwiPalette.yellow[500],
		hoverBg: xwiPalette.yellow[700],
	},
	impersonation: {
		main: xwiPalette.yellow[500],
		contrastText: xwiPalette.common.black,
	},
	subscriptionExpiration: {
		warning60Days: xwiPalette.orange[200],
		warning30Days: xwiPalette.yellow[200],
	},
	subscriptionStatus: {
		active: xwiPalette.green[500],
		trial: xwiPalette.violet[600],
		canceled: xwiPalette.red[600],
		created: xwiPalette.yellow[600],
		suspended: xwiPalette.grey[500],
		expired: xwiPalette.grey[500],
		pendingCancelation: xwiPalette.grey[500],
	},
	recommendedActions: {
		highlight: xwiPalette.yellow[800],
	},
	iconStack: {
		filled: {
			bgColor: xwiPalette.grey[400],
			iconColor: xwiPalette.grey[900],
			fontColor: xwiPalette.grey[900],
		},
		outlined: {
			bgColor: xwiPalette.grey[900],
			iconColor: xwiPalette.grey[400],
			fontColor: xwiPalette.grey[400],
		},
	},
	timeline: { greyzone: xwiPalette.grey[700] },
	animatableLayer: xwiPalette.green[400],
	locationQualityWarning: xwiPalette.grey[300],
	mapStyleSelected: xwiPalette.blue[600],
}
